// P1-12: every scan carries its own identity, built on the scanning device.
//
// Shape: {sanitized_lot}-{YYYYMMDDTHHmmss}-{scan#:03}
// Example: 0309Edge-20260729T091422-003
//
// Generated client-side deliberately, for two reasons. The client explicitly asked
// for the *device's* local scan time, which only the device knows; and an identity
// minted before the request is sent is the natural idempotency key for P1-7's offline
// sync, because a retried upload cannot double-insert a scan that already carries its
// own id. The server enforces uniqueness on it.
//
// The timestamp is deliberately DEVICE-LOCAL wall clock, not UTC. Do not reach for
// toISOString() here: it converts to UTC and would print the wrong hour (and, near
// midnight, the wrong day) on the report the driver is reading. This codebase already
// has an off-by-one-day bug from exactly that confusion. The unambiguous instant is
// sent separately as device_scanned_at, and the server records its own
// scan_received_at, which is what anything authoritative orders by.

const pad = (value, width) => String(value).padStart(width, '0');

// Lot numbers are free text and end up inside an identifier, so strip anything that
// would make the uid ambiguous to split on or awkward in a filename.
const sanitizeLot = (lot) => {
    const cleaned = String(lot ?? '').replace(/[^A-Za-z0-9]/g, '');
    return cleaned === '' ? 'NOLOT' : cleaned;
};

// YYYYMMDDTHHmmss from the device's own clock.
const deviceLocalStamp = (date = new Date()) =>
    `${date.getFullYear()}${pad(date.getMonth() + 1, 2)}${pad(date.getDate(), 2)}`
    + `T${pad(date.getHours(), 2)}${pad(date.getMinutes(), 2)}${pad(date.getSeconds(), 2)}`;

const buildScanUid = (lot, scanNumber, date = new Date()) =>
    `${sanitizeLot(lot)}-${deviceLocalStamp(date)}-${pad(scanNumber, 3)}`;

// The device's best guess at the next sequence number, from the rows it has loaded.
// Only used to fill the uid's trailing segment: the authoritative scan_number is
// assigned by the server, which is the only party that can see two devices at once.
const nextLocalScanSeq = (assets) => {
    // Number(null) is 0 rather than NaN, so uncollected and legacy rows have to be
    // discarded before the numeric check rather than by it.
    const numbers = (Array.isArray(assets) ? assets : [])
        .filter(asset => asset != null && asset.scan_number != null)
        .map(asset => Number(asset.scan_number))
        .filter(number => Number.isFinite(number));
    return (numbers.length === 0 ? 0 : Math.max(...numbers)) + 1;
};

// Everything a scan needs to send. Kept together so the uid and the instant it
// describes can never drift apart at a call site.
const buildScanIdentity = (lot, scanNumber, date = new Date()) => ({
    scan_uid: buildScanUid(lot, scanNumber, date),
    device_scanned_at: date.toISOString()
});

const exports = {
    sanitizeLot,
    deviceLocalStamp,
    buildScanUid,
    nextLocalScanSeq,
    buildScanIdentity,
};

export default exports;
export {
    sanitizeLot,
    deviceLocalStamp,
    buildScanUid,
    nextLocalScanSeq,
    buildScanIdentity,
};
